import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Row, Col } from 'antd';
import { Form, FormikProvider, useFormik } from 'formik';
import isEqual from 'lodash.isequal';
import * as Yup from 'yup';
import { useStore } from '../../../store';
import { useActions } from '../../providers/actions-provider/actions.context';
import FormControl from '../../lib/form-control';
import LeavePageModal from '../../lib/leave-page-modal';
import InputSelect from '../../lib/input-select';
import checkPermission from '../../../utils/permissions';

const FirstTab = (): JSX.Element => {
  // hook
  const { executedReport, reportCollections } = useStore();
  const { reportActions } = useActions();
  const { location: { pathname } } = useHistory();
  // id for action
  const positionOfExec = pathname.indexOf('exec-');
  const positionOfID = pathname.indexOf('/', positionOfExec + 5);
  const id = pathname
    .slice(positionOfExec + 5, positionOfID === -1 ? pathname.length : positionOfID);
  // hook
  useEffect(() => {
    async function getReportData() {
      await reportActions.getExecutedReport(+id);
      await reportActions.getAllColumnsForReport(executedReport.data.collection);
      await reportActions.getCollectionsForReport();
    }
    getReportData();
  }, [id]);

  const validationSchema = Yup.object({
    code: Yup.string()
      .matches(
        /^[A-Z0-9_]*$/,
        'Code must contain only uppercase letters, numbers, and underscores',
      )
      .required('Code is required'),
  });

  const formikForFirstStep = useFormik({
    initialValues: {
      id: executedReport.data.id,
      tenant: executedReport.data.tenant,
      description: executedReport.data.description,
      find: executedReport.data.find,
      sort: executedReport.data.sort,
      columns: executedReport.data.columns,
      name: executedReport.data.name,
      code: executedReport.data.code,
      collection: executedReport.data.collection,
      aggregate: executedReport.data.aggregate,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    onSubmit: async (values) => {
      await reportActions.updateExecutedReport(+id, values);
      reportActions.getExecutedReport(+id);
    },
    enableReinitialize: true,
  });

  const {
    values,
    initialValues,
    isSubmitting,
    setFieldValue,
    submitForm,
  } = formikForFirstStep;
  const valuesIsEqual = isEqual(values, initialValues);

  const preparedCollections = reportCollections.data.map((collection, index) => ({
    id: index + 1,
    name: collection,
  }));
  const hasNotWritePermission = !checkPermission('reports', 'write');
  return (
    executedReport.data.name ? (
      <FormikProvider value={formikForFirstStep}>
        {!isSubmitting && !valuesIsEqual && !hasNotWritePermission && <LeavePageModal />}
        <Form
          className="formik-form"
          style={{ width: '100%' }}
        >
          <Row gutter={[10, 0]}>
            <Col style={{ minWidth: '300px' }}>
              <FormControl
                inputClassName="formik-form__input"
                name="name"
                label="Name"
                type="text"
                disabled={hasNotWritePermission}
              />
              <FormControl
                inputClassName="formik-form__input"
                name="description"
                label="Description (optional)"
                type="text"
                disabled={hasNotWritePermission}
              />
            </Col>
            <Col style={{ minWidth: '300px' }}>
              <FormControl
                inputClassName="formik-form__input"
                name="code"
                label="Code"
                type="text"
                disabled={hasNotWritePermission}
              />
              <InputSelect
                disabled={hasNotWritePermission}
                title="Collection"
                defaultValue={executedReport.data.collection}
                fieldName="collection"
                onChange={(value: string | number) => {
                  // eslint-disable-next-line array-callback-return
                  reportCollections.data.find((el) => {
                    if (el === value) {
                      setFieldValue('collection', el);
                    }
                  });
                }}
                data={preparedCollections}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormControl
                disabled={hasNotWritePermission}
                inputClassName="formik-form__input"
                name="aggregate"
                label="Aggregate (optional)"
                type="textarea"
              />
            </Col>
          </Row>
        </Form>

        <Button
          onClick={() => submitForm()}
          style={{ margin: '20px 0 0' }}
          disabled={isSubmitting || valuesIsEqual || hasNotWritePermission}
          htmlType="submit"
          type="primary"
          className="formik-form__submit"
        >
          Save
        </Button>
      </FormikProvider>
    ) : <></>
  );
};

FirstTab.displayName = 'FirstTab';

export default FirstTab;
